import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
   createInstructorSubstitution,
   deleteInstructorSubstitution,
   getInstructorSubstitutions,
   updateInstructorSubstitution,
} from "../../api/instructorSubstitutionsService";
import { scheduleCalendarRefresh } from "../Utils/calendarBus";

const EMPTY_LIST = [];

const pad2 = (value) => String(value).padStart(2, "0");

function normName(value) {
   return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
}

function monthKey(date) {
   const d = date instanceof Date ? date : new Date(date);
   if (Number.isNaN(d.getTime())) return "";
   return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

function parseMonthKey(value) {
   const match = String(value || "").match(/^(\d{4})-(\d{2})$/);
   if (!match) return null;
   const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
   return Number.isNaN(date.getTime()) ? null : date;
}

function dateInputValueFromAny(value) {
   if (!value) return "";
   if (typeof value === "string") {
      const direct = value.match(/^(\d{4}-\d{2}-\d{2})/);
      if (direct?.[1]) return direct[1];
   }
   const parsed = new Date(value);
   if (Number.isNaN(parsed.getTime())) return "";
   return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(
      parsed.getDate(),
   )}`;
}

function getDefaultSubstitutionDate(monthValue) {
   const monthKeyValue = String(monthValue || monthKey(new Date())).trim();
   const today = dateInputValueFromAny(new Date());
   if (monthKeyValue && today.startsWith(`${monthKeyValue}-`)) return today;
   if (/^\d{4}-\d{2}$/.test(monthKeyValue)) return `${monthKeyValue}-01`;
   return today;
}

function getMonthDateRange(monthValue) {
   const date = parseMonthKey(monthValue);
   if (!date) return null;
   const start = new Date(date.getFullYear(), date.getMonth(), 1);
   const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
   return {
      dateFrom: dateInputValueFromAny(start),
      dateTo: dateInputValueFromAny(end),
   };
}

function getInstructorDisplayName(inst) {
   const full = `${inst?.firstName ?? ""} ${inst?.lastName ?? ""}`.trim();
   if (full) return full;
   const fallback = String(inst?.name ?? "").trim();
   return fallback || "Instructor";
}

function getSubstitutionId(row) {
   return String(row?.id ?? row?._id ?? row?.substitutionId ?? "").trim();
}

function getSubstitutionBaseId(row) {
   return String(
      row?.baseInstructorId ??
         row?.base_instructor_id ??
         row?.baseInstructor?.id ??
         row?.base_instructor?.id ??
         "",
   ).trim();
}

function getSubstitutionSubstituteId(row) {
   return String(
      row?.substituteInstructorId ??
         row?.substitute_instructor_id ??
         row?.substituteInstructor?.id ??
         row?.substitute_instructor?.id ??
         "",
   ).trim();
}

function isSubstitutionRowActive(row) {
   return row?.isActive !== false && row?.is_active !== false;
}

/**
 * Finds a deactivated substitution for the same base instructor and day.
 * The backend keeps one row per (baseInstructorId, date) even after it is
 * deactivated, so a new one cannot be created — it has to be reactivated.
 */
function findInactiveSubstitutionForSlot(rows, baseInstructorId, date) {
   const baseId = String(baseInstructorId || "").trim();
   const dateKey = String(date || "").trim();
   if (!baseId || !dateKey) return null;

   return (
      (Array.isArray(rows) ? rows : EMPTY_LIST).find(
         (row) =>
            !isSubstitutionRowActive(row) &&
            getSubstitutionBaseId(row) === baseId &&
            dateInputValueFromAny(row?.date) === dateKey,
      ) || null
   );
}

function buildUsedSubstituteMap(rows, editingId = "") {
   const map = new Map();
   const list = Array.isArray(rows) ? rows : EMPTY_LIST;
   for (const row of list) {
      if (!isSubstitutionRowActive(row)) continue;
      const substituteId = getSubstitutionSubstituteId(row);
      const rowId = getSubstitutionId(row);
      if (!substituteId) continue;
      if (editingId && rowId === editingId) continue;
      const baseName = getSubstitutionInstructorName(
         row,
         "baseInstructor",
         getSubstitutionBaseId(row),
      );
      map.set(substituteId, baseName);
   }
   return map;
}

function orderSubstituteOptions(options, usedSubstituteMap) {
   const available = [];
   const used = [];
   for (const option of Array.isArray(options) ? options : EMPTY_LIST) {
      const usedForBase = usedSubstituteMap.get(option.value) || "";
      const enriched = {
         ...option,
         isUsedOnDate: Boolean(usedForBase),
         usedForBaseName: usedForBase,
      };
      if (enriched.isUsedOnDate) used.push(enriched);
      else available.push(enriched);
   }
   return [...available, ...used];
}

function getSubstitutionInstructorName(row, key, fallbackId) {
   const inst =
      row?.[key] ??
      (key === "baseInstructor"
         ? row?.base_instructor
         : key === "substituteInstructor"
           ? row?.substitute_instructor
           : null);
   const name = getInstructorDisplayName(inst);
   if (name && name !== "Instructor") return name;
   return fallbackId ? `#${fallbackId}` : "Instructor";
}

function parsePositiveInt(value) {
   const parsed = Number(value);
   return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function normalizeIdForApi(value) {
   const parsed = parsePositiveInt(value);
   return parsed ?? String(value || "").trim();
}

function buildInitialForm({
   currentMonthValue,
   baseInstructorOptions,
   substituteInstructorOptions,
   initialForm,
}) {
   const baseOptions = Array.isArray(baseInstructorOptions)
      ? baseInstructorOptions
      : EMPTY_LIST;
   const substituteOptions = Array.isArray(substituteInstructorOptions)
      ? substituteInstructorOptions
      : EMPTY_LIST;
   const monthValue = String(currentMonthValue || "").trim();
   const initialDate = String(initialForm?.date || "").trim();

   return {
      date:
         initialDate &&
         (!monthValue || initialDate.startsWith(`${monthValue}-`))
            ? initialDate
            : getDefaultSubstitutionDate(monthValue),
      baseInstructorId:
         String(initialForm?.baseInstructorId || "").trim() ||
         baseOptions[0]?.value ||
         "",
      substituteInstructorId:
         String(initialForm?.substituteInstructorId || "").trim() ||
         substituteOptions[0]?.value ||
         "",
   };
}

function formatSubstituteOptionSubtitle(option) {
   if (option?.isUsedOnDate) {
      const baseName = String(option?.usedForBaseName || "").trim();
      return baseName
         ? `Deja inlocuieste pe ${baseName}`
         : "Deja utilizat in aceasta zi";
   }
   const sector = String(option?.sector || "").trim();
   const gearbox = String(option?.gearbox || "").trim();
   const parts = [sector, gearbox].filter(Boolean);
   return parts.length ? parts.join(" · ") : "Sector / cutie nesetate";
}

function formatInstructorMetaLine(value, fallback) {
   const text = String(value || "").trim();
   return text || fallback;
}

function formatQuickDateLabel(value) {
   const raw = String(value || "").trim();
   if (!raw) return "Data lipsa";
   const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
   if (!match) return raw;
   const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
   if (Number.isNaN(date.getTime())) return raw;
   return new Intl.DateTimeFormat("ro-RO", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
   }).format(date);
}

function renderSubstituteSearchList({
   substituteOptions,
   filteredSubstituteOptions,
   selectedId,
   saving,
   onSelect,
}) {
   if (substituteOptions.length === 0) {
      return (
         <li className="instructorSubstitutionsPopup__searchEmpty">
            Nu exista instructori cu tipul SCHIMB.
         </li>
      );
   }

   if (!filteredSubstituteOptions.length) {
      return (
         <li className="instructorSubstitutionsPopup__searchEmpty">
            Nu exista rezultate pentru cautare.
         </li>
      );
   }

   return filteredSubstituteOptions.map((option) => {
      const selected = option.value === selectedId;
      const disabled = Boolean(option.isUsedOnDate);
      return (
         <li
            key={option.value}
            className={[
               "instructorSubstitutionsPopup__searchItem",
               selected ? "instructorSubstitutionsPopup__searchItem--highlighted" : "",
               disabled ? "instructorSubstitutionsPopup__searchItem--disabled" : "",
            ]
               .filter(Boolean)
               .join(" ")}
            role={disabled ? "presentation" : "button"}
            tabIndex={saving || disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            onClick={() => {
               if (saving || disabled) return;
               onSelect(option.value);
            }}
            onKeyDown={(event) => {
               if (event.key !== "Enter" && event.key !== " ") return;
               event.preventDefault();
               if (saving || disabled) return;
               onSelect(option.value);
            }}
         >
            <h3>{option.label}</h3>
            <p>{formatSubstituteOptionSubtitle(option)}</p>
         </li>
      );
   });
}

export default function InstructorSubstitutionsPopup({
   mode = "manager",
   currentMonthValue = "",
   baseInstructorOptions = EMPTY_LIST,
   substituteInstructorOptions = EMPTY_LIST,
   quickContext = null,
   initialForm = null,
   onChanged,
   onAlert,
   onClose,
}) {
   const baseOptions = Array.isArray(baseInstructorOptions)
      ? baseInstructorOptions
      : EMPTY_LIST;
   const substituteOptions = Array.isArray(substituteInstructorOptions)
      ? substituteInstructorOptions
      : EMPTY_LIST;
   const isQuickMode = mode === "quick";

   const [rows, setRows] = useState([]);
   const [loading, setLoading] = useState(false);
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState("");
   const [editingId, setEditingId] = useState("");
   const [form, setForm] = useState(() =>
      buildInitialForm({
         currentMonthValue,
         baseInstructorOptions: baseOptions,
         substituteInstructorOptions: substituteOptions,
         initialForm,
      }),
   );
   const [substituteSearch, setSubstituteSearch] = useState("");
   const [daySubstitutions, setDaySubstitutions] = useState(EMPTY_LIST);
   const [daySubstitutionsLoading, setDaySubstitutionsLoading] = useState(false);

   const existingQuickSubstitution = useMemo(() => {
      if (!isQuickMode) return null;
      const baseId = String(
         form?.baseInstructorId || quickContext?.instructorId || "",
      ).trim();
      const date = String(form?.date || "").trim();
      if (!baseId || !date) return null;

      return (
         daySubstitutions.find((row) => {
            if (!isSubstitutionRowActive(row)) return false;
            const rowBaseId = getSubstitutionBaseId(row);
            const rowDate = dateInputValueFromAny(row?.date);
            return rowBaseId === baseId && rowDate === date;
         }) || null
      );
   }, [
      daySubstitutions,
      form?.baseInstructorId,
      form?.date,
      isQuickMode,
      quickContext?.instructorId,
   ]);
   const existingQuickSubstitutionId = getSubstitutionId(
      existingQuickSubstitution,
   );
   const effectiveEditingId =
      editingId || (isQuickMode ? existingQuickSubstitutionId : "");

   const usedSubstituteMap = useMemo(
      () => buildUsedSubstituteMap(daySubstitutions, effectiveEditingId),
      [daySubstitutions, effectiveEditingId],
   );
   const orderedSubstituteOptions = useMemo(
      () => orderSubstituteOptions(substituteOptions, usedSubstituteMap),
      [substituteOptions, usedSubstituteMap],
   );

   const baseInstructorLabel =
      String(quickContext?.instructorName || "").trim() ||
      baseOptions.find((option) => option.value === form?.baseInstructorId)
         ?.label ||
      (form?.baseInstructorId ? `#${form.baseInstructorId}` : "Instructor");
   const selectedBaseInstructor = baseOptions.find(
      (option) => option.value === form?.baseInstructorId,
   );
   const baseInstructorSector = formatInstructorMetaLine(
      quickContext?.instructorSector ?? selectedBaseInstructor?.sector,
      "Sector nesetat",
   );
   const baseInstructorGearbox = formatInstructorMetaLine(
      quickContext?.instructorGearbox ?? selectedBaseInstructor?.gearbox,
      "Cutie nesetata",
   );
   const quickDateLabel = formatQuickDateLabel(form?.date);
   const selectedSubstitute = orderedSubstituteOptions.find(
      (option) => option.value === form?.substituteInstructorId,
   );
   const selectedSubstituteIsUsed = Boolean(
      usedSubstituteMap.get(form?.substituteInstructorId || ""),
   );
   const existingSubstituteInstructor =
      existingQuickSubstitution?.substituteInstructor ??
      existingQuickSubstitution?.substitute_instructor ??
      null;
   const activeSubstituteLabel =
      selectedSubstitute?.label ||
      (existingQuickSubstitution
         ? getSubstitutionInstructorName(
              existingQuickSubstitution,
              "substituteInstructor",
              getSubstitutionSubstituteId(existingQuickSubstitution),
           )
         : "") ||
      (form?.substituteInstructorId
         ? `#${form.substituteInstructorId}`
         : "Neales");
   const selectedSubstituteSector = formatInstructorMetaLine(
      selectedSubstitute?.sector ?? existingSubstituteInstructor?.sector,
      "Sector nesetat",
   );
   const selectedSubstituteGearbox = formatInstructorMetaLine(
      selectedSubstitute?.gearbox ?? existingSubstituteInstructor?.gearbox,
      "Cutie nesetata",
   );
   const hasActiveQuickShift = Boolean(existingQuickSubstitutionId);
   const substituteStatusLabel = hasActiveQuickShift
      ? "Schimb activ"
      : form?.substituteInstructorId
        ? "Selectat"
        : "Fără schimb";

   const filteredSubstituteOptions = useMemo(() => {
      const q = normName(substituteSearch);
      if (!q) return orderedSubstituteOptions;
      return orderedSubstituteOptions.filter((option) =>
         normName(
            [
               option.label,
               option.value,
               option.sector,
               option.gearbox,
               option.usedForBaseName,
            ]
               .filter(Boolean)
               .join(" "),
         ).includes(q),
      );
   }, [orderedSubstituteOptions, substituteSearch]);
   const canSave =
      !saving &&
      !loading &&
      !daySubstitutionsLoading &&
      String(form?.date || "").trim() &&
      String(form?.baseInstructorId || "").trim() &&
      String(form?.substituteInstructorId || "").trim() &&
      !selectedSubstituteIsUsed;

   const notifyChanged = useCallback(
      (message, detail = {}) => {
         scheduleCalendarRefresh({ type: "substitutions-changed" });
         onChanged?.({ message, ...detail });
         onAlert?.("success", message);
      },
      [onAlert, onChanged],
   );

   const resetForm = useCallback(() => {
      setEditingId("");
      setError("");
      setForm(
         buildInitialForm({
            currentMonthValue,
            baseInstructorOptions: baseOptions,
            substituteInstructorOptions: substituteOptions,
            initialForm,
         }),
      );
   }, [baseOptions, currentMonthValue, initialForm, substituteOptions]);

   const loadDaySubstitutions = useCallback(async (dateValue) => {
      const date = String(dateValue || "").trim();
      if (!date) {
         setDaySubstitutions(EMPTY_LIST);
         return EMPTY_LIST;
      }
      setDaySubstitutionsLoading(true);
      try {
         // Fetched without the isActive filter: deactivated rows still occupy
         // the (base instructor, day) slot on the server and must be visible
         // here, otherwise saving hits a 409 the user cannot explain.
         const result = await getInstructorSubstitutions({ date });
         const rows = Array.isArray(result) ? result : EMPTY_LIST;
         setDaySubstitutions(rows);
         return rows;
      } catch {
         setDaySubstitutions(EMPTY_LIST);
         return EMPTY_LIST;
      } finally {
         setDaySubstitutionsLoading(false);
      }
   }, []);

   const loadRows = useCallback(async () => {
      const range = getMonthDateRange(currentMonthValue);
      if (!range) return;
      setLoading(true);
      setError("");
      try {
         const result = await getInstructorSubstitutions({
            ...range,
            isActive: true,
         });
         setRows(Array.isArray(result) ? result : []);
      } catch (err) {
         setError(err?.message || "Nu am putut incarca inlocuirile.");
      } finally {
         setLoading(false);
      }
   }, [currentMonthValue]);

   const refreshSubstitutionState = useCallback(
      async (dateValue) => {
         const date = String(dateValue || form?.date || "").trim();
         await loadRows();
         if (date) {
            await loadDaySubstitutions(date);
         }
      },
      [form?.date, loadDaySubstitutions, loadRows],
   );

   useEffect(() => {
      setSubstituteSearch("");
   }, [mode, form?.baseInstructorId, form?.date]);

   useEffect(() => {
      void loadDaySubstitutions(form?.date);
   }, [form?.date, loadDaySubstitutions]);

   useEffect(() => {
      if (!isQuickMode || !existingQuickSubstitution) return;
      const nextId = getSubstitutionId(existingQuickSubstitution);
      if (!nextId) return;
      const nextSubstituteId = getSubstitutionSubstituteId(
         existingQuickSubstitution,
      );
      setEditingId((prev) => (prev === nextId ? prev : nextId));
      setForm((prev) => {
         const currentSubstitute = String(
            prev?.substituteInstructorId || "",
         ).trim();
         if (currentSubstitute === nextSubstituteId) return prev;
         return {
            ...(prev || {}),
            substituteInstructorId: nextSubstituteId,
         };
      });
   }, [existingQuickSubstitution, isQuickMode]);

   useEffect(() => {
      if (!selectedSubstituteIsUsed) return;
      // Don't wipe the substitute that belongs to the shift we're editing.
      const activeSubId = getSubstitutionSubstituteId(existingQuickSubstitution);
      if (
         activeSubId &&
         activeSubId === String(form?.substituteInstructorId || "").trim()
      ) {
         return;
      }
      setForm((prev) => ({
         ...(prev || {}),
         substituteInstructorId: "",
      }));
   }, [
      existingQuickSubstitution,
      form?.substituteInstructorId,
      selectedSubstituteIsUsed,
   ]);

   useEffect(() => {
      if (isQuickMode) return;
      loadRows();
   }, [isQuickMode, loadRows]);

   const handleChange = useCallback((field, value) => {
      const key = String(field || "").trim();
      if (!key) return;
      setForm((prev) => ({
         ...(prev || {}),
         [key]: String(value || ""),
      }));
   }, []);

   const handleEdit = useCallback(
      (row) => {
         const id = getSubstitutionId(row);
         if (!id) return;
         setEditingId(id);
         setError("");
         setForm({
            date:
               dateInputValueFromAny(row?.date) ||
               getDefaultSubstitutionDate(currentMonthValue),
            baseInstructorId: getSubstitutionBaseId(row),
            substituteInstructorId: getSubstitutionSubstituteId(row),
         });
      },
      [currentMonthValue],
   );

   const handleSubmit = useCallback(
      async (event) => {
         event?.preventDefault?.();
         const date = String(form?.date || "").trim();
         const baseInstructorId = String(form?.baseInstructorId || "").trim();
         const substituteInstructorId = String(
            form?.substituteInstructorId || "",
         ).trim();

         if (!date || !baseInstructorId || !substituteInstructorId) {
            setError("Completeaza data si ambii instructori.");
            return;
         }
         if (baseInstructorId === substituteInstructorId) {
            setError("Instructorul de baza si cel de schimb difera.");
            return;
         }

         const latestDayRows = await loadDaySubstitutions(date);
         const saveEditingId = editingId || existingQuickSubstitutionId;
         const latestUsedMap = buildUsedSubstituteMap(
            latestDayRows,
            saveEditingId,
         );
         const usedForBase = latestUsedMap.get(substituteInstructorId);
         if (usedForBase) {
            setError(
               usedForBase
                  ? `${selectedSubstitute?.label || "Acest instructor de schimb"} inlocuieste deja pe ${usedForBase} in aceasta zi.`
                  : "Acest instructor de schimb este deja utilizat in aceasta zi.",
            );
            return;
         }

         // Un rând dezactivat pentru aceeași zi și același instructor de bază
         // blochează crearea pe server, deci îl reactivăm în loc să creăm.
         const reusableId =
            saveEditingId ||
            getSubstitutionId(
               findInactiveSubstitutionForSlot(
                  latestDayRows,
                  baseInstructorId,
                  date,
               ),
            );

         setSaving(true);
         setError("");
         try {
            if (reusableId) {
               const saved = await updateInstructorSubstitution(reusableId, {
                  substituteInstructorId: normalizeIdForApi(
                     substituteInstructorId,
                  ),
                  date,
                  isActive: true,
               });
               notifyChanged("Inlocuirea a fost actualizata.", {
                  action: "update",
                  substitution:
                     saved && typeof saved === "object"
                        ? saved
                        : {
                             id: reusableId,
                             baseInstructorId,
                             substituteInstructorId,
                             date,
                             isActive: true,
                          },
               });
            } else {
               const saved = await createInstructorSubstitution({
                  baseInstructorId: normalizeIdForApi(baseInstructorId),
                  substituteInstructorId: normalizeIdForApi(
                     substituteInstructorId,
                  ),
                  date,
               });
               notifyChanged("Inlocuirea a fost creata.", {
                  action: "create",
                  substitution:
                     saved && typeof saved === "object"
                        ? saved
                        : {
                             baseInstructorId,
                             substituteInstructorId,
                             date,
                             isActive: true,
                          },
               });
            }

            resetForm();
            if (isQuickMode) {
               onClose?.();
            } else {
               await loadRows();
            }
         } catch (err) {
            setError(err?.message || "Nu am putut salva inlocuirea.");
         } finally {
            setSaving(false);
         }
      },
      [
         editingId,
         existingQuickSubstitutionId,
         form,
         isQuickMode,
         loadDaySubstitutions,
         loadRows,
         notifyChanged,
         onClose,
         resetForm,
         selectedSubstitute?.label,
      ],
   );

   const handleDeactivate = useCallback(
      async (row) => {
         const id = getSubstitutionId(row);
         if (!id) return;
         setSaving(true);
         setError("");
         try {
            await updateInstructorSubstitution(id, { isActive: false });
            notifyChanged("Inlocuirea a fost dezactivata.", {
               action: "deactivate",
               substitutionId: id,
               date: row?.date,
               baseInstructorId: getSubstitutionBaseId(row),
            });
            if (editingId === id) resetForm();
            await refreshSubstitutionState(row?.date);
         } catch (err) {
            setError(err?.message || "Nu am putut dezactiva inlocuirea.");
         } finally {
            setSaving(false);
         }
      },
      [editingId, notifyChanged, refreshSubstitutionState, resetForm],
   );

   const handleDelete = useCallback(
      async (row) => {
         const id = getSubstitutionId(row);
         if (!id) return;
         const confirmed =
            typeof window === "undefined" ||
            typeof window.confirm !== "function" ||
            window.confirm("Stergi permanent aceasta inlocuire?");
         if (!confirmed) return;

         setSaving(true);
         setError("");
         try {
            await deleteInstructorSubstitution(id);
            notifyChanged("Inlocuirea a fost stearsa.", {
               action: "delete",
               substitutionId: id,
               date: row?.date,
               baseInstructorId: getSubstitutionBaseId(row),
            });
            if (editingId === id) resetForm();
            await refreshSubstitutionState(row?.date);
         } catch (err) {
            setError(err?.message || "Nu am putut sterge inlocuirea.");
         } finally {
            setSaving(false);
         }
      },
      [editingId, notifyChanged, refreshSubstitutionState, resetForm],
   );

   const handleQuickCancelShift = useCallback(async () => {
      if (!existingQuickSubstitution) return;
      const id = getSubstitutionId(existingQuickSubstitution);
      if (!id) return;

      setSaving(true);
      setError("");
      try {
         await deleteInstructorSubstitution(id);
         notifyChanged("Schimbul a fost anulat.", {
            action: "delete",
            substitutionId: id,
            date: existingQuickSubstitution?.date,
            baseInstructorId: getSubstitutionBaseId(existingQuickSubstitution),
         });
         setEditingId("");
         setForm((prev) => ({
            ...(prev || {}),
            substituteInstructorId: "",
         }));
         await refreshSubstitutionState(existingQuickSubstitution?.date);
      } catch (err) {
         setError(err?.message || "Nu am putut anula schimbul.");
      } finally {
         setSaving(false);
      }
   }, [existingQuickSubstitution, notifyChanged, refreshSubstitutionState]);

   const title = isQuickMode ? "Inlocuire instructor" : "Instructori de schimb";

   return (
      <section
         className="instructorSubstitutionsPopup"
         aria-label={title}
      >
         <div className="instructorSubstitutionsPopup__header">
            <h3 className="instructorSubstitutionsPopup__title">{title}</h3>
            {!isQuickMode ? (
               <p className="instructorSubstitutionsPopup__subtitle">Lecții practice</p>
            ) : null}
         </div>

         <form
            className="instructorSubstitutionsPopup__content instructorSubstitutionsPopup__form"
            onSubmit={handleSubmit}
         >
            {isQuickMode ? (
               <>
                  <div className="instructorSubstitutionsPopup__group">
                     <div className="instructorSubstitutionsPopup__row instructorSubstitutionsPopup__row--four">
                        <div className="instructorSubstitutionsPopup__field">
                           <span className="instructorSubstitutionsPopup__label">
                              Ziua
                           </span>
                           <span className="instructorSubstitutionsPopup__value">
                              {quickDateLabel}
                           </span>
                        </div>
                        <div className="instructorSubstitutionsPopup__field">
                           <span className="instructorSubstitutionsPopup__label">
                              Instructor
                           </span>
                           <span className="instructorSubstitutionsPopup__value">
                              {baseInstructorLabel}
                           </span>
                        </div>
                        <div className="instructorSubstitutionsPopup__field">
                           <span className="instructorSubstitutionsPopup__label">
                              Cutia
                           </span>
                           <span className="instructorSubstitutionsPopup__value">
                              {baseInstructorGearbox}
                           </span>
                        </div>
                        <div className="instructorSubstitutionsPopup__field">
                           <span className="instructorSubstitutionsPopup__label">
                              Sectorul
                           </span>
                           <span className="instructorSubstitutionsPopup__value">
                              {baseInstructorSector}
                           </span>
                        </div>
                     </div>
                  </div>

                  <div
                     className={[
                        "instructorSubstitutionsPopup__group",
                        hasActiveQuickShift
                           ? "instructorSubstitutionsPopup__group--activeShift"
                           : "",
                     ]
                        .filter(Boolean)
                        .join(" ")}
                  >
                     <div className="instructorSubstitutionsPopup__row instructorSubstitutionsPopup__row--four">
                        <div className="instructorSubstitutionsPopup__field">
                           <span className="instructorSubstitutionsPopup__label">
                              Status
                           </span>
                           <span className="instructorSubstitutionsPopup__value">
                              {substituteStatusLabel}
                           </span>
                        </div>
                        <div className="instructorSubstitutionsPopup__field">
                           <span className="instructorSubstitutionsPopup__label">
                              Înlocuitor
                           </span>
                           <span className="instructorSubstitutionsPopup__value">
                              {activeSubstituteLabel}
                           </span>
                        </div>
                        <div className="instructorSubstitutionsPopup__field">
                           <span className="instructorSubstitutionsPopup__label">
                              Cutia
                           </span>
                           <span className="instructorSubstitutionsPopup__value">
                              {form?.substituteInstructorId ||
                              hasActiveQuickShift
                                 ? selectedSubstituteGearbox
                                 : "—"}
                           </span>
                        </div>
                        <div className="instructorSubstitutionsPopup__field">
                           <span className="instructorSubstitutionsPopup__label">
                              Sectorul
                           </span>
                           <span className="instructorSubstitutionsPopup__value">
                              {form?.substituteInstructorId ||
                              hasActiveQuickShift
                                 ? selectedSubstituteSector
                                 : "—"}
                           </span>
                        </div>
                     </div>
                  </div>
               </>
            ) : (
               <div className="instructorSubstitutionsPopup__group">
                  <div className="instructorSubstitutionsPopup__row instructorSubstitutionsPopup__row--three">
                     <label className="instructorSubstitutionsPopup__field">
                        <span className="instructorSubstitutionsPopup__label">
                           Data
                        </span>
                        <input
                           type="date"
                           className="instructorSubstitutionsPopup__input"
                           value={form?.date || ""}
                           onChange={(event) =>
                              handleChange("date", event.target.value)
                           }
                           disabled={saving}
                        />
                     </label>
                     <label className="instructorSubstitutionsPopup__field">
                        <span className="instructorSubstitutionsPopup__label">
                           Instructor baza
                        </span>
                        <select
                           className="instructorSubstitutionsPopup__select"
                           value={form?.baseInstructorId || ""}
                           onChange={(event) =>
                              handleChange("baseInstructorId", event.target.value)
                           }
                           disabled={saving || Boolean(editingId)}
                        >
                           <option value="">Alege instructorul</option>
                           {baseOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                 {option.label}
                              </option>
                           ))}
                        </select>
                     </label>
                     <label className="instructorSubstitutionsPopup__field">
                        <span className="instructorSubstitutionsPopup__label">
                           Instructor schimb
                        </span>
                        <select
                           className="instructorSubstitutionsPopup__select"
                           value={form?.substituteInstructorId || ""}
                           onChange={(event) =>
                              handleChange(
                                 "substituteInstructorId",
                                 event.target.value,
                              )
                           }
                           disabled={saving}
                        >
                           <option value="">Alege inlocuitorul</option>
                           {orderedSubstituteOptions.map((option) => (
                              <option
                                 key={option.value}
                                 value={option.value}
                                 disabled={option.isUsedOnDate}
                              >
                                 {option.isUsedOnDate
                                    ? `${option.label} (utilizat)`
                                    : option.label}
                              </option>
                           ))}
                        </select>
                     </label>
                  </div>
               </div>
            )}

            {isQuickMode ? (
               <>
                  <div className="instructorSubstitutionsPopup__group instructorSubstitutionsPopup__group--search">
                     <div className="instructorSubstitutionsPopup__searchHeader">
                        <input
                           type="text"
                           className="instructorSubstitutionsPopup__searchInput"
                           placeholder="Cauta instructor de schimb..."
                           value={substituteSearch}
                           onChange={(event) =>
                              setSubstituteSearch(event.target.value)
                           }
                           disabled={saving}
                           autoFocus
                        />
                     </div>
                  </div>

                  <div className="instructorSubstitutionsPopup__group instructorSubstitutionsPopup__group--list">
                     <ul className="instructorSubstitutionsPopup__searchList">
                        {renderSubstituteSearchList({
                           substituteOptions: orderedSubstituteOptions,
                           filteredSubstituteOptions,
                           selectedId: form?.substituteInstructorId,
                           saving,
                           onSelect: (value) =>
                              handleChange("substituteInstructorId", value),
                        })}
                     </ul>
                  </div>
               </>
            ) : null}

            {error ? (
               <p className="instructorSubstitutionsPopup__error" role="alert">
                  {error}
               </p>
            ) : null}

            <div className="instructorSubstitutionsPopup__group">
               {isQuickMode ? (
                  <div
                     className={[
                        "instructorSubstitutionsPopup__actions",
                        "instructorSubstitutionsPopup__actions--double",
                     ].join(" ")}
                  >
                     <button
                        type="button"
                        className="instructorSubstitutionsPopup__btn instructorSubstitutionsPopup__btn--normal"
                        onClick={handleQuickCancelShift}
                        disabled={saving || !hasActiveQuickShift}
                     >
                        Anulează schimbul
                     </button>
                     <button
                        type="submit"
                        className="instructorSubstitutionsPopup__btn instructorSubstitutionsPopup__btn--save"
                        disabled={!canSave}
                     >
                        {saving
                           ? "Se salvează..."
                           : hasActiveQuickShift
                             ? "Actualizează"
                             : selectedSubstitute
                               ? "Confirmă schimbul"
                               : "Alege instructorul"}
                     </button>
                  </div>
               ) : (
                  <div
                     className={[
                        "instructorSubstitutionsPopup__actions",
                        editingId
                           ? "instructorSubstitutionsPopup__actions--triple"
                           : "instructorSubstitutionsPopup__actions--double",
                     ].join(" ")}
                  >
                     {editingId ? (
                        <button
                           type="button"
                           className="instructorSubstitutionsPopup__btn instructorSubstitutionsPopup__btn--normal"
                           onClick={resetForm}
                           disabled={saving}
                        >
                           Anuleaza
                        </button>
                     ) : null}
                     <button
                        type="button"
                        className="instructorSubstitutionsPopup__btn instructorSubstitutionsPopup__btn--normal"
                        onClick={loadRows}
                        disabled={loading || saving}
                     >
                        Reincarca
                     </button>
                     <button
                        type="submit"
                        className="instructorSubstitutionsPopup__btn instructorSubstitutionsPopup__btn--primary"
                        disabled={!canSave}
                     >
                        {saving
                           ? "Se salveaza..."
                           : editingId
                             ? "Salveaza"
                             : "Creeaza"}
                     </button>
                  </div>
               )}
            </div>
         </form>

         {!isQuickMode ? (
            <div className="instructorSubstitutionsPopup__list">
               {loading ? (
                  <p className="instructorSubstitutionsPopup__state">
                     Se incarca...
                  </p>
               ) : rows.length ? (
                  rows.map((row) => {
                     const id = getSubstitutionId(row);
                     const baseId = getSubstitutionBaseId(row);
                     const substituteId = getSubstitutionSubstituteId(row);
                     const date = dateInputValueFromAny(row?.date);
                     const baseName = getSubstitutionInstructorName(
                        row,
                        "baseInstructor",
                        baseId,
                     );
                     const substituteName = getSubstitutionInstructorName(
                        row,
                        "substituteInstructor",
                        substituteId,
                     );

                     return (
                        <div
                           key={id || `${baseId}-${substituteId}-${date}`}
                           className="instructorSubstitutionsPopup__item"
                        >
                           <div className="instructorSubstitutionsPopup__itemMain">
                              <p className="instructorSubstitutionsPopup__itemDate">
                                 {date || "Data lipsa"}
                              </p>
                              <p className="instructorSubstitutionsPopup__itemText">
                                 {baseName} → {substituteName}
                              </p>
                           </div>
                           <div className="instructorSubstitutionsPopup__itemActions">
                              <button
                                 type="button"
                                 className="instructorSubstitutionsPopup__itemBtn"
                                 onClick={() => handleEdit(row)}
                                 disabled={saving}
                              >
                                 Editare
                              </button>
                              <button
                                 type="button"
                                 className="instructorSubstitutionsPopup__itemBtn"
                                 onClick={() => handleDeactivate(row)}
                                 disabled={saving || !id}
                              >
                                 Dezactiveaza
                              </button>
                              <button
                                 type="button"
                                 className="instructorSubstitutionsPopup__itemBtn instructorSubstitutionsPopup__itemBtn--danger"
                                 onClick={() => handleDelete(row)}
                                 disabled={saving || !id}
                              >
                                 Sterge
                              </button>
                           </div>
                        </div>
                     );
                  })
               ) : (
                  <p className="instructorSubstitutionsPopup__listEmpty">
                     Nu exista inlocuiri active in luna selectata.
                  </p>
               )}
            </div>
         ) : null}
      </section>
   );
}
